import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import {
  getAdminOnboardingSlides,
  createAdminOnboardingSlide,
  updateAdminOnboardingSlide,
  deleteAdminOnboardingSlide,
  reorderAdminOnboardingSlides,
} from "../../../api/apiServices";

const VISUAL_OPTIONS = [
  { value: "crypto",   label: "Crypto Coins",    icon: "ri-bit-coin-line",         desc: "Floating coin badges with NGN orb" },
  { value: "exchange", label: "Exchange Flow",    icon: "ri-arrow-up-down-line",    desc: "NGN to crypto swap cards" },
  { value: "bank",     label: "Bank Withdrawal",  icon: "ri-bank-line",             desc: "Balance + bank grid" },
  { value: "bills",    label: "Bill Payments",    icon: "ri-receipt-line",          desc: "Service categories grid" },
];

const labelCls = "text-white/40 text-[10px] uppercase tracking-wide block mb-1.5 font-semibold";
const inputCls = "w-full bg-[#111] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 transition-colors";

// ── Confirm modal ─────────────────────────────────────────────
function ConfirmModal({ title, message, loading, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center space-y-2">
          <p className="text-white font-bold">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Slide modal (add / edit) ───────────────────────────────────
function SlideModal({ mode, slide, onClose, onSaved }) {
  const [form, setForm]     = useState({
    title:    slide?.title    || "",
    subtitle: slide?.subtitle || "",
    pills:    slide?.pills?.join(", ") || "",  // edit as comma-separated string
    visual:   slide?.visual   || "crypto",
    isActive: slide?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.subtitle.trim()) {
      toast.error("Title and subtitle are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title:    form.title.trim(),
        subtitle: form.subtitle.trim(),
        pills:    form.pills.split(",").map(p => p.trim()).filter(Boolean),
        visual:   form.visual,
        isActive: form.isActive,
      };
      if (mode === "add") {
        await createAdminOnboardingSlide(payload);
        toast.success("Slide created");
      } else {
        await updateAdminOnboardingSlide(slide._id, payload);
        toast.success("Slide updated");
      }
      onSaved();
      onClose();
    } catch { toast.error("Failed to save slide"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70">
      <div className="w-full sm:max-w-lg bg-[#161616] border border-white/8 rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 flex-shrink-0">
          <p className="text-white font-bold text-sm">{mode === "add" ? "Add Slide" : "Edit Slide"}</p>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/60" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Title */}
          <div>
            <label className={labelCls}>Title <span className="text-white/20">(use \n for line break)</span></label>
            <textarea value={form.title} onChange={e => set("title", e.target.value)} rows={2}
              placeholder={"Buy Crypto\nwith Naira"}
              className={inputCls + " resize-none font-mono text-sm"} />
          </div>

          {/* Subtitle */}
          <div>
            <label className={labelCls}>Subtitle</label>
            <textarea value={form.subtitle} onChange={e => set("subtitle", e.target.value)} rows={3}
              placeholder="Short description shown below the title…"
              className={inputCls + " resize-none leading-relaxed"} />
          </div>

          {/* Pills */}
          <div>
            <label className={labelCls}>Pills <span className="text-white/20">(comma separated)</span></label>
            <input value={form.pills} onChange={e => set("pills", e.target.value)}
              placeholder="BTC, ETH, USDT, BNB" className={inputCls} />
            {/* Preview */}
            {form.pills.trim() && (
              <div className="flex flex-wrap gap-2 mt-2">
                {form.pills.split(",").filter(p => p.trim()).map((p, i) => (
                  <span key={i} className="text-[10px] font-semibold px-2.5 py-1 rounded-full bg-[#00C896]/10 text-[#00C896] border border-[#00C896]/20">
                    {p.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Visual selector */}
          <div>
            <label className={labelCls}>Visual</label>
            <div className="grid grid-cols-2 gap-2">
              {VISUAL_OPTIONS.map(v => (
                <button key={v.value} onClick={() => set("visual", v.value)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    form.visual === v.value
                      ? "border-[#00C896] bg-[#00C896]/10"
                      : "border-white/8 bg-white/4 hover:bg-white/8"
                  }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${v.icon} text-sm ${form.visual === v.value ? "text-[#00C896]" : "text-white/40"}`} />
                    <span className={`text-xs font-bold ${form.visual === v.value ? "text-[#00C896]" : "text-white/60"}`}>{v.label}</span>
                  </div>
                  <p className="text-white/25 text-[10px] leading-snug">{v.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className={labelCls}>Status</label>
            <div className="flex gap-2">
              {[{ v: true, l: "Active" }, { v: false, l: "Hidden" }].map(o => (
                <button key={String(o.v)} onClick={() => set("isActive", o.v)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                    form.isActive === o.v
                      ? o.v ? "bg-[#00C896]/15 text-[#00C896] border-[#00C896]/30" : "bg-white/5 text-white/50 border-white/10"
                      : "bg-white/4 text-white/30 border-white/8 hover:bg-white/8"
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 px-6 pb-6 pt-4 border-t border-white/5 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#00C896] hover:bg-[#00b386] text-black text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" /> : null}
            {saving ? "Saving…" : mode === "add" ? "Create Slide" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function OnboardingView() {
  const [slides,  setSlides]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);   // null | { mode, slide? }
  const [confirm, setConfirm] = useState(null);   // { id, title }
  const [deleting,setDeleting]= useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminOnboardingSlides();
      if (res.status) setSlides(res.data);
    } catch { toast.error("Failed to load slides"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAdminOnboardingSlide(confirm.id);
      setSlides(prev => prev.filter(s => s._id !== confirm.id));
      setConfirm(null);
      toast.success("Slide deleted");
    } catch { toast.error("Failed to delete slide"); }
    finally { setDeleting(false); }
  };

  const handleToggle = async (slide) => {
    try {
      await updateAdminOnboardingSlide(slide._id, { isActive: !slide.isActive });
      setSlides(prev => prev.map(s => s._id === slide._id ? { ...s, isActive: !slide.isActive } : s));
      toast.success(slide.isActive ? "Slide hidden" : "Slide activated");
    } catch { toast.error("Failed to update slide"); }
  };

  const handleMoveUp = async (slide, idx) => {
    if (idx === 0) return;
    const prev  = slides[idx - 1];
    const newOrder = [
      { id: slide._id, order: prev.order },
      { id: prev._id,  order: slide.order },
    ];
    await reorderAdminOnboardingSlides(newOrder).catch(() => {});
    load();
  };

  const handleMoveDown = async (slide, idx) => {
    if (idx === slides.length - 1) return;
    const next = slides[idx + 1];
    const newOrder = [
      { id: slide._id, order: next.order },
      { id: next._id,  order: slide.order },
    ];
    await reorderAdminOnboardingSlides(newOrder).catch(() => {});
    load();
  };

  const visualIcon = (v) => VISUAL_OPTIONS.find(o => o.value === v)?.icon || "ri-image-line";
  const visualLabel = (v) => VISUAL_OPTIONS.find(o => o.value === v)?.label || v;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">Onboarding Slides</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {slides.filter(s => s.isActive).length} active · {slides.filter(s => !s.isActive).length} hidden
          </p>
        </div>
        <button onClick={() => setModal({ mode: "add" })}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00C896] hover:bg-[#00b386] text-black text-xs font-bold rounded-xl transition-colors">
          <i className="ri-add-line" />Add Slide
        </button>
      </div>

      {/* Slides list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-[#161616] rounded-2xl border border-white/5 animate-pulse" />)}
        </div>
      ) : slides.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <i className="ri-slideshow-line text-white/10 text-5xl mb-3" />
          <p className="text-white/25 text-sm">No slides yet</p>
          <button onClick={() => setModal({ mode: "add" })} className="mt-3 text-[#00C896] text-xs font-semibold">
            + Add the first slide
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {slides.map((slide, idx) => (
            <div key={slide._id}
              className={`bg-[#161616] border rounded-2xl p-4 transition-all ${slide.isActive ? "border-white/5" : "border-white/5 opacity-50"}`}>
              <div className="flex items-start gap-4">
                {/* Reorder arrows */}
                <div className="flex flex-col gap-1 flex-shrink-0 mt-0.5">
                  <button onClick={() => handleMoveUp(slide, idx)} disabled={idx === 0}
                    className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/30 disabled:opacity-20 transition-colors">
                    <i className="ri-arrow-up-s-line text-sm" />
                  </button>
                  <button onClick={() => handleMoveDown(slide, idx)} disabled={idx === slides.length - 1}
                    className="w-6 h-6 rounded bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/30 disabled:opacity-20 transition-colors">
                    <i className="ri-arrow-down-s-line text-sm" />
                  </button>
                </div>

                {/* Slide number */}
                <div className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 text-white/40 text-xs font-bold">
                  {idx + 1}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="text-white text-sm font-semibold whitespace-pre-line leading-tight"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {slide.title.replace(/\\n/g, " · ")}
                    </p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${slide.isActive ? "bg-[#00C896]/15 text-[#00C896]" : "bg-white/5 text-white/30"}`}>
                      {slide.isActive ? "Active" : "Hidden"}
                    </span>
                  </div>
                  <p className="text-white/35 text-xs leading-relaxed line-clamp-1 mb-2">{slide.subtitle}</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Visual badge */}
                    <span className="flex items-center gap-1 text-[10px] text-white/40 bg-white/5 px-2 py-1 rounded-lg">
                      <i className={`${visualIcon(slide.visual)} text-[11px]`} />
                      {visualLabel(slide.visual)}
                    </span>
                    {/* Pills */}
                    {slide.pills?.slice(0, 4).map(p => (
                      <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/30">{p}</span>
                    ))}
                    {slide.pills?.length > 4 && <span className="text-[10px] text-white/20">+{slide.pills.length - 4} more</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => handleToggle(slide)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                      slide.isActive ? "bg-[#00C896]/10 text-[#00C896]" : "bg-white/5 text-white/30"
                    }`} title={slide.isActive ? "Hide" : "Activate"}>
                    <i className={`${slide.isActive ? "ri-eye-line" : "ri-eye-off-line"} text-sm`} />
                  </button>
                  <button onClick={() => setModal({ mode: "edit", slide })}
                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/15 flex items-center justify-center text-white/40 hover:text-white transition-colors">
                    <i className="ri-pencil-line text-sm" />
                  </button>
                  <button onClick={() => setConfirm({ id: slide._id, title: slide.title.replace(/\\n/g, " ") })}
                    className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/20 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors">
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {modal && <SlideModal mode={modal.mode} slide={modal.slide} onClose={() => setModal(null)} onSaved={load} />}
      {confirm && (
        <ConfirmModal
          title="Delete Slide"
          message={`Delete "${confirm.title}"? This cannot be undone.`}
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
